import type { ICreateOfferPayload } from "@api";
import type { IOffer } from "@bbj-types/offer";
import type { IVariable } from "@bbj-types/utils";
import { CreateWithAIModal, type ICreateWithAIModalLabels } from "@complex/CreateWithAIModal";
import type { TAppellationOption } from "@complex/FormGen/Components/Inputs/Appellation";
import type { TContactValue } from "@complex/FormGen/Components/Inputs/Contact";
import type { TEnterpriseOption } from "@complex/FormGen/Components/Inputs/Enterprise";
import { useGlobalContext } from "@root/context";
import { OfferKind } from "@root/utils/enums/offer";
import { useI18nContext } from "@root/utils/organisation";
import { useMemo } from "react";
import type { FieldValues } from "react-hook-form";
import { useAIGeneration } from "../logics";
import { parseOfferFromAI, useCreateAndPinOffers, useEnterpriseContactsLookup, useOfferUrlLookup } from "./logics";
import { offerFormSchema } from "./schema";

export type TOfferForm = Omit<
  ICreateOfferPayload["offer"],
  | "contractType"
  | "contractDuration"
  | "appellationCode"
  | "appellationLabel"
  | "romeCode"
  | "romeLabel"
  | "externalSource"
> &
  Omit<ICreateOfferPayload, "offer" | "createOffer" | "starCandidate"> & {
    contractType: IVariable;
    contractDurationValue?: number;
    contractDurationUnit?: IVariable;
    appellation?: TAppellationOption;
    externalSource?: IVariable;
    enterprise?: TEnterpriseOption;
    contact?: TContactValue;
  };

const LABELS: ICreateWithAIModalLabels = {
  title: "Importer du texte pour générer l'offre d'emploi via IA",
  instruction:
    "Copier coller le texte de l'offre que vous avez trouvé dans l'encart ci-dessous puis cliquer sur « Afficher la/ les offres générées » pour remplir automatiquement les champs de création d'une offre.",
  generate: "Afficher la/les offres générées",
  confirm: "Créer et épingler",
  items: "Offres générées",
  generating: "Offre(s) en cours de génération",
  empty: "Aucune offre n'a été détectée dans ce texte.",
  failed: (failedCount, total) => `Échec de génération pour ${failedCount} offre(s) sur ${total}.`,
};

export interface IModalOfferFromAIProps {
  open: boolean;
  candidateId?: string;
  onClose: () => void;
  onConfirm: (items: IOffer[]) => void;
}

export const ModalOfferFromAI = ({ open, candidateId, onClose, onConfirm }: IModalOfferFromAIProps) => {
  const { phoneCode } = useI18nContext();
  const parseItem = useMemo(() => parseOfferFromAI(phoneCode), [phoneCode]);
  const { generation, handleGenerate, reset } = useAIGeneration(OfferKind.JOB, parseItem);
  const { settingsV2 } = useGlobalContext();
  const { urlContext, getMatchedOffer } = useOfferUrlLookup(settingsV2);
  const enterpriseContactsLookup = useEnterpriseContactsLookup();

  const context = useMemo(
    () => ({
      settings: {
        settings: {
          ...(settingsV2.settingsContractTypes
            ? { "contract-types": { ...settingsV2.settingsContractTypes, settingType: "contract-types" } }
            : {}),
          ...(settingsV2.settingsOfferOrigin
            ? // biome-ignore lint/style/useNamingConvention: key must match backend settingType
              { offer_origin: { ...settingsV2.settingsOfferOrigin, settingType: "offer_origin" } }
            : {}),
        },
      },
      url: urlContext,
      contacts: { contact: enterpriseContactsLookup },
    }),
    [settingsV2.settingsContractTypes, settingsV2.settingsOfferOrigin, urlContext, enterpriseContactsLookup]
  );
  const { createAndPinOffers, loading } = useCreateAndPinOffers(candidateId, getMatchedOffer);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleConfirm = async (offers: TOfferForm[]) => {
    const { created, failed, failedIndexes } = await createAndPinOffers(offers);
    if (failed.length > 0) {
      window.publish("feedback", {
        severity: "error",
        message: `Échec de création pour ${failed.length} offre(s) sur ${offers.length}.`,
      });
    }
    if (created.length > 0) {
      window.publish("feedback", {
        severity: "success",
        message: `${created.length} offre(s) créée(s) et épinglée(s).`,
      });
    }
    if (failed.length === 0) {
      reset();
      onConfirm(created);
      return;
    }
    // Seules les offres en échec restent dans la modale, avec les modifications de l'utilisateur
    return failedIndexes;
  };

  return (
    <CreateWithAIModal<FieldValues, TOfferForm>
      open={open}
      labels={LABELS}
      generation={generation}
      schema={offerFormSchema}
      context={context}
      onClose={handleClose}
      onGenerate={handleGenerate}
      onConfirm={handleConfirm}
      loading={loading}
    />
  );
};
